import { RightioWBSAPIModelsSPSpUsersGetStatisticsResult } from "@utils/apis/wbs/model";

const createLeagueTable = (data: Required<RightioWBSAPIModelsSPSpUsersGetStatisticsResult>[]): Array<LeagueTable> =>
  data.map((item) => ({
    userId: item.userId,
    name: String(item.name),
    hitRate: item.hitRate || 0,
    cardRate: item.cardRate || 0,
    carePlanRate: item.carePlanRate || 0,
    emailRate: item.emailRate || 0,
    reducedRate: item.reducedRate || 0,
    abandonedRate: item.abandonRate || 0,
  }));

interface LeagueTable {
  userId: number;
  name: string;
  hitRate: number;
  cardRate: number;
  carePlanRate: number;
  emailRate: number;
  reducedRate: number;
  abandonedRate: number;
}

export const refreshLeagueData = (data: Required<RightioWBSAPIModelsSPSpUsersGetStatisticsResult>[]) => {
  // drop Totals row
  data = data.filter((item) => item.userId !== 0);
  // drop Website row
  data = data.filter((item) => item.name !== "Website" && item.name !== "Total");
  const leagueTable = createLeagueTable(data);
  const hitRates = leagueTable.map((item) => ({ rate: item.hitRate, name: item.name })).sort((a, b) => b.rate - a.rate);
  const cardRates = leagueTable
    .map((item) => ({ rate: item.cardRate, name: item.name }))
    .sort((a, b) => b.rate - a.rate);
  const carePlanRates = leagueTable
    .map((item) => ({ rate: item.carePlanRate, name: item.name }))
    .sort((a, b) => b.rate - a.rate);
  const emailRates = leagueTable
    .map((item) => ({ rate: item.emailRate, name: item.name }))
    .sort((a, b) => b.rate - a.rate);
  const reducedRates = leagueTable
    .map((item) => ({ rate: item.reducedRate, name: item.name }))
    .sort((a, b) => b.rate - a.rate);
  const abandonedRates = leagueTable
    .map((item) => ({ rate: item.abandonedRate, name: item.name }))
    .sort((a, b) => b.rate - a.rate);

  let hitRateTable = new Set();
  let cardRateTable = new Set();
  let carePlanRateTable = new Set();
  let emailRateTable = new Set();
  let reducedRateTable = new Set();
  let abandonedRateTable = new Set();

  if (hitRates.length > 3) {
    hitRateTable = new Set([
      ...hitRates.slice(0, 3),
      hitRates[3],
      ...hitRates.slice(hitRates.length - 3, hitRates.length),
    ]);
  } else {
    hitRateTable = new Set(hitRates);
  }

  if (cardRates.length > 3) {
    cardRateTable = new Set([
      ...cardRates.slice(0, 3),
      cardRates[3],
      ...cardRates.slice(cardRates.length - 3, cardRates.length),
    ]);
  } else {
    cardRateTable = new Set(cardRates);
  }

  if (carePlanRates.length > 3) {
    carePlanRateTable = new Set([
      ...carePlanRates.slice(0, 3),
      carePlanRates[3],
      ...carePlanRates.slice(carePlanRates.length - 3, carePlanRates.length),
    ]);
  } else {
    carePlanRateTable = new Set(carePlanRates);
  }

  if (emailRates.length > 3) {
    emailRateTable = new Set([
      ...emailRates.slice(0, 3),
      emailRates[3],
      ...emailRates.slice(emailRates.length - 3, emailRates.length),
    ]);
  } else {
    emailRateTable = new Set(emailRates);
  }

  if (reducedRates.length > 3) {
    reducedRateTable = new Set([
      ...reducedRates.slice(0, 3),
      reducedRates[3],
      ...reducedRates.slice(reducedRates.length - 3, reducedRates.length),
    ]);
  } else {
    reducedRateTable = new Set(reducedRates);
  }

  if (abandonedRates.length > 3) {
    abandonedRateTable = new Set([
      ...abandonedRates.slice(0, 3),
      abandonedRates[3],
      ...abandonedRates.slice(abandonedRates.length - 3, abandonedRates.length),
    ]);
  } else {
    abandonedRateTable = new Set(abandonedRates);
  }

  const leagueTableData = [
    {
      name: "Hit Rate",
      data: [...hitRateTable],
    },
    {
      name: "Card Rate",
      data: [...cardRateTable],
    },
    {
      name: "Care Plan Rate",
      data: [...carePlanRateTable],
    },
    {
      name: "Email Rate",
      data: [...emailRateTable],
    },
    {
      name: "Reduced Rate",
      data: [...reducedRateTable],
    },
    {
      name: "Abandoned Rate",
      data: [...abandonedRateTable],
    },
  ];

  return leagueTableData;
};

// userId	name	departmentId	totalJobs	callsForHitRate	opportunities	bookings	EmailCollected	CardDetailsCollected	JobsReduced	NoiseCalls	Fails	Quotes	GoBacks	CarePlans	AbandonRate	HitRate	NoiseRate	ReducedRate	CardRate	EmailRate	CarePlanRate	OpportunityRate	BookingRate
const someData = [
  [0, "Server", 0, 4, 4, 4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 0],
  [53, "Stephen Chandail", 11, 20, 20, 20, 6, 5, 4, 2, 10, 4, 0, 0, 0, 0, 30, 166, 33, 66, 83, 0, 100, 30],
  [275, "Azir Alam", 4, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [783, "Sabrina Marie", 11, 24, 24, 24, 10, 10, 8, 3, 9, 5, 0, 0, 1, 0, 42, 90, 30, 80, 100, 10, 100, 41],
  [2543, "Ajmal Ali", 11, 16, 15, 15, 11, 8, 5, 10, 3, 1, 1, 0, 4, 0, 73, 27, 90, 45, 72, 36, 93, 73],
  [2571, "Paul Holt", 11, 29, 29, 29, 15, 11, 13, 3, 9, 4, 0, 0, 1, 0, 52, 60, 20, 86, 73, 6, 100, 51],
  [2589, "James Medford", 11, 20, 19, 19, 7, 7, 4, 4, 10, 2, 1, 0, 0, 0, 37, 142, 57, 57, 100, 0, 95, 36],
  [2599, "Kate Bradley", 11, 34, 34, 34, 12, 10, 11, 1, 18, 4, 0, 0, 1, 0, 35, 150, 8, 91, 83, 8, 100, 35],
  [2617, "Cory Wheeler", 16, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 100, 0],
  [2625, "Harry Johnson", 11, 35, 34, 34, 13, 9, 6, 5, 16, 4, 1, 0, 2, 0, 38, 123, 38, 46, 69, 15, 97, 38],
  [2627, "Jabari Powell", 11, 41, 37, 37, 16, 14, 6, 4, 17, 4, 3, 1, 0, 0, 43, 106, 25, 37, 87, 0, 90, 43],
  [2631, "Brandon Prince", 11, 41, 37, 37, 7, 7, 4, 0, 17, 12, 3, 1, 0, 0, 19, 242, 0, 57, 100, 0, 90, 18],
  [2638, "Saiqa Bibi", 11, 19, 19, 19, 3, 0, 2, 0, 8, 5, 0, 0, 0, 0, 16, 266, 0, 66, 0, 0, 100, 15],
  [2639, "Lydia Turner", 11, 30, 29, 29, 11, 8, 7, 5, 14, 4, 1, 0, 1, 0, 38, 127, 45, 63, 72, 9, 96, 37],
  [2640, "Samuel Lee", 11, 28, 27, 27, 8, 2, 1, 2, 12, 7, 1, 0, 0, 0, 30, 150, 25, 12, 25, 0, 96, 29],
  [2641, "Chetan Walia", 11, 22, 20, 20, 3, 3, 2, 1, 12, 6, 2, 0, 0, 0, 15, 400, 33, 66, 100, 0, 90, 15],
  [8888, "Website", 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

// transoform the data into a usable format
export const transformData = () => {
  const transformedData: RightioWBSAPIModelsSPSpUsersGetStatisticsResult[] = someData.map((item) => ({
    userId: Number(item[0]),
    name: String(item[1]),
    departmentId: Number(item[2]),
    totalJobs: Number(item[3]),
    callsForHitRate: Number(item[4]),
    opportunities: Number(item[5]),
    bookings: Number(item[6]),
    emailCollected: Number(item[7]),
    cardDetailsCollected: Number(item[8]),
    jobsReduced: Number(item[9]),
    noiseCalls: Number(item[10]),
    fails: Number(item[11]),
    quotes: Number(item[12]),
    goBacks: Number(item[13]),
    carePlans: Number(item[14]),
    abandonRate: Number(item[15]),
    hitRate: Number(item[16]),
    noiseRate: Number(item[17]),
    reducedRate: Number(item[18]),
    cardRate: Number(item[19]),
    emailRate: Number(item[20]),
    carePlanRate: Number(item[21]),
    opportunityRate: Number(item[22]),
    bookingRate: Number(item[23]),
  }));
  return transformedData;
};
